import { CosmosApp } from "./engine/CosmosApp.ts";
import { colorMap } from "./engine/render/Links.ts";
import { Hud } from "./ui/hud.ts";
import { Inspector } from "./ui/inspector.ts";
import { Search } from "./ui/search.ts";
import { Breadcrumbs } from "./ui/breadcrumbs.ts";
import { Favorites } from "./ui/favorites.ts";
import { Minimap } from "./ui/minimap.ts";
import { Agent } from "./ui/agent.ts";
import { Navigator } from "./nav/navigator.ts";
import type { NodeDetail } from "./engine/types.ts";
import { installShortcuts } from "./ui/shortcuts.ts";
import { Timeline } from "./ui/timeline.ts";
import { Jarvis } from "./ui/jarvis.ts";
import type { CosmosData, GalaxyKey } from "./parser/types.ts";

const DATA_URL = "cosmos.json";
const POLL_MS = 5000;

async function fetchData(): Promise<CosmosData> {
  // Autossuficiência: tenta o BANCO (API) primeiro; cai para o JSON estático (resiliência).
  try {
    const r = await fetch(`/api/graph?t=${Date.now()}`, { cache: "no-store" });
    if (r.ok) { const d = await r.json(); if (d?.nodes?.length) return d; }
  } catch { /* sem API: usa o estático */ }
  const r = await fetch(`${DATA_URL}?t=${Date.now()}`, { cache: "no-store" });
  if (!r.ok) throw new Error(`cosmos.json ${r.status}`);
  return r.json();
}
const stampOf = (d: CosmosData & { ingestedAt?: string }) => `${d.ingestedAt ?? ""}|${d.generatedAt}`;

function mountHelp(root: HTMLElement, toggle: () => void) {
  root.insertAdjacentHTML("beforeend", `
    <div id="help"><div class="help-card panel">
      <h3>Atalhos</h3>
      <ul>
        <li><kbd>/</kbd> ou <kbd>⌘K</kbd><span>buscar</span></li>
        <li><kbd>clique</kbd><span>focar nó / abrir inspector</span></li>
        <li><kbd>scroll</kbd><span>zoom contínuo</span></li>
        <li><kbd>arrastar</kbd><span>navegar (com inércia)</span></li>
        <li><kbd>Alt</kbd>+<kbd>←</kbd> / <kbd>→</kbd><span>histórico voltar/avançar</span></li>
        <li><kbd>F</kbd><span>favoritar o nó atual</span></li>
        <li><kbd>+</kbd> / <kbd>−</kbd><span>zoom</span></li>
        <li><kbd>Esc</kbd><span>visão geral</span></li>
        <li><kbd>?</kbd><span>esta ajuda</span></li>
      </ul>
      <button id="help-close">fechar</button>
    </div></div>`);
  root.querySelector("#help-close")!.addEventListener("click", toggle);
  root.querySelector("#help")!.addEventListener("click", (e) => { if (e.target === e.currentTarget) toggle(); });
}

async function boot() {
  const root = document.getElementById("cosmos-root") as HTMLElement;
  const data = await fetchData();
  let colorFn = colorMap(data);

  const cosmos = new CosmosApp(data, {
    onFocus: (gal) => hud.setFocusTitle(gal as GalaxyKey | null),
    onSelect: (d) => { inspector.show(d); breadcrumbs.update(d); nav.onSelected(d?.id ?? null); },
    onFrame: (cam, scene) => minimap.draw(cam, scene),
  });
  const nav = new Navigator(cosmos);
  const hud = new Hud(data, { onToggleGalaxy: (g, on) => cosmos.toggleGalaxy(g, on) });
  const favorites = new Favorites(data, {
    resolve: (id) => cosmos.nodeRef(id),
    onNavigate: (id) => nav.navigate(id),
  });
  const agent = new Agent();
  const buildContext = (d: NodeDetail): string => {
    const lines = [`Domínio: ${d.galLabel} (${d.kind === "council" ? "núcleo do sistema" : "galáxia/domínio"})`];
    if (d.metrics.length) lines.push(`KPIs: ${d.metrics.map((m) => `${m.label}=${m.value}`).join(" · ")}`);
    if (d.children.length) lines.push(`Itens (${d.children.length}): ${d.children.map((c) => c.label).slice(0, 60).join(", ")}`);
    if (d.connections.length) lines.push(`Conexões: ${d.connections.map((c) => c.label).slice(0, 40).join(", ")}`);
    if (Object.keys(d.meta).length) lines.push(`Propriedades: ${Object.entries(d.meta).map(([k, v]) => `${k}=${v}`).join("; ")}`);
    return lines.join("\n");
  };
  const inspector = new Inspector(data, {
    onNavigate: (id) => nav.navigate(id),
    isFavorite: (id) => favorites.has(id),
    onToggleFavorite: (id) => favorites.toggle(id),
    onAgent: (id) => {
      const d = cosmos.detailOf(id);
      if (d) agent.open(d.kind === "council" ? "Strategic Council" : d.galLabel, buildContext(d));
    },
  });
  const search = new Search(data, {
    query: (q) => cosmos.queryNodes(q),
    onPick: (id) => nav.navigate(id),
  });
  const breadcrumbs = new Breadcrumbs({
    onNavigate: (id) => nav.navigate(id),
    onBack: () => nav.back(),
    onForward: () => nav.forward(),
    onHome: () => nav.home(),
  });
  const minimap = new Minimap({ onJump: (wx, wy) => cosmos.cam.flyTo(wx, wy, cosmos.cam.z, 700) });
  nav.onChange = (b, f) => breadcrumbs.setHistory(b, f);

  await cosmos.init(root);
  minimap.setColor(colorFn);
  const timeline = new Timeline();
  const jarvis = new Jarvis(data);
  hud.mount(root); breadcrumbs.mount(root); search.mount(root);
  inspector.mount(root); favorites.mount(root); minimap.mount(root); agent.mount(root); timeline.mount(root); jarvis.mount(root);

  const toggleHelp = () => document.getElementById("help")!.classList.toggle("open");
  mountHelp(root, toggleHelp);

  installShortcuts({
    focusSearch: () => search.focus(),
    home: () => nav.home(),
    back: () => nav.back(),
    forward: () => nav.forward(),
    zoomIn: () => cosmos.zoomBy(1.25),
    zoomOut: () => cosmos.zoomBy(1 / 1.25),
    reset: () => nav.home(),
    toggleFavorite: () => { const id = cosmos.selectionId; if (id) { favorites.toggle(id); inspector.refresh(); } },
    toggleHelp,
  });

  (window as unknown as { cosmos: CosmosApp }).cosmos = cosmos;

  // espelho fiel do Vault (clone completo) — reconhecimento total no dashboard
  fetch(`vault-mirror.json?t=${Date.now()}`, { cache: "no-store" })
    .then((r) => (r.ok ? r.json() : null))
    .then((m) => { if (m) cosmos.setMirror(m); })
    .catch(() => { /* opcional */ });

  // ---- sincronização automática com o Vault ----
  let lastStamp = stampOf(data);
  setInterval(async () => {
    try {
      const next = await fetchData();
      if (stampOf(next) === lastStamp) return;
      lastStamp = stampOf(next);
      colorFn = colorMap(next);
      cosmos.refresh(next);
      hud.refresh(next);
      inspector.setData(next); inspector.show(null);
      search.setData(next);
      favorites.setData(next); favorites.render();
      breadcrumbs.update(null);
      minimap.setColor(colorFn);
      cosmos.flashSync();
    } catch { /* transitório */ }
  }, POLL_MS);
}

boot().catch((e) => {
  console.error("[cosmos] falha no boot:", e);
  document.body.insertAdjacentHTML(
    "beforeend",
    `<pre style="position:fixed;inset:20px;color:#f88;font:12px monospace;white-space:pre-wrap;z-index:99">${String(e?.stack || e)}</pre>`,
  );
});
